import {
	BackdropCircularProgress,
	DeleteConfirmationDialog,
	EnhancedDropDownRHF,
	EnhancedSnackbar,
	IEnhancedDropDownItem,
	IResponse,
	IServerError,
	ITest,
	StandardPanel,
	TwoColumnLayout,
} from "@websential/cosmic-v20";
import {
	DeductionsSetupFieldsPanel,
	DeterminingGrossIncomeSetupFieldsPanel,
	EarningsSetupFieldsPanel,
	ReimbursementsSetupFieldsPanel,
} from "./Panels";
import { FC, useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { Grid, useTheme } from "@mui/material";
import { scrollPanelFieldsToTop, sortDDLByLabel, useNavigateToLogout } from "../../common/Common";
import { useDispatch, useSelector } from "react-redux";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate, useParams } from "react-router-dom";

import { IAppState } from "../../store/reducers";
import { IDropDownListResponse } from "../../models/common/IDropDownListResponse";
import { INITIAL_SNACKBAR_DATA } from "../../common/constants";
import { ISalaryProfileRequestPayload } from "../../models";
import { PermissionsService } from "../../services/PermissionsService";
import { ResourceDDLDataTableForSlips } from "../ResourceDDLDataTableForSlips";
import { SalaryProfileService } from "../../services/SalaryProfileService";
import { SalaryProfileToolbar } from "./SalaryProfileToolbar";
import { SalaryProfileValidationSchema } from "./validator";
import { TaxCreditsSetupFieldsPanel } from "./Panels/TaxCreditsSetupFieldsPanel";
import { TaxExemptionsSetupFieldsPanel } from "./Panels/TaxExemptionsSetupFieldsPanel";
import { WCBService } from "../../services/WCBService";
import { WSIBOntarioService } from "../../services/WSIBOntarioService";
import { setSelectedDrawerOptionsMenu } from "../../store/actions/drawerMenuOptionsActions";
import { setSelectedStickyNavBar } from "../../store/actions";
import { useGetPayrollFrequenciesDDL } from "../PayrollSchedule";
import { yupResolver } from "@hookform/resolvers/yup";

export interface ISalaryProfile extends ITest { }

const SINGLE_RECORD_DATA_QUERY_KEY = "salary-profile-single-data-record";
const EMPLOYEE_RECORD_DATA_QUERY_KEY = "employee-single-data-record";
const EDR_EARNINGS_AND_TIME_FORMAT_DATA_QUERY_KEY =
	"edr-earnings-single-data-record";
const EDR_RECORD_DATA_QUERY_KEY = "edr-single-data-record";
const EMPLOYEE_DDL_BY_STATUS_DATA_QUERY_KEY = "employee-ddl-by-status";
const SALARY_PROFILE_DDL_BY_EMPLOYEE_ID_DATA_QUERY_KEY =
	"salary-profile-ddl-by-employee-id";
const PROFILE_APPLIES_FROM_PAY_PERIOD_DDL_DATA_QUERY_KEY =
	"profile-applies-from-pay-period-ddl";
const EMPLOYEE_DDL_DATA_QUERY_KEY = "employee-ddl-data-record";

const useGetData = (
	id: string | undefined,
	clientId: string,
	taxYear: number
) =>
	useQuery({
		queryKey: [SINGLE_RECORD_DATA_QUERY_KEY, id],
		queryFn: (data) => {
			return SalaryProfileService.getSalaryProfileById(id, clientId, taxYear);
		},
		refetchOnWindowFocus: false,
	});

const useGetEmployeeInformationById = (id: string | undefined) =>
	useQuery({
		queryKey: [EMPLOYEE_RECORD_DATA_QUERY_KEY, id],
		queryFn: (data) => {
			return SalaryProfileService.getEmployeeInformationById(id);
		},
		refetchOnWindowFocus: false,
	});

const useGetEDREarningsAndTimeFormatData = (
	clientId: string,
	taxYear: number
) =>
	useQuery({
		queryKey: [EDR_EARNINGS_AND_TIME_FORMAT_DATA_QUERY_KEY],
		queryFn: (data) => {
			return SalaryProfileService.getEDREarningsAndTimeFormatData(
				clientId,
				taxYear
			);
		},
		refetchOnWindowFocus: false,
	});

const useGetEmployeeDDLByStatus = (
	clientId: string,
	taxYear: number,
	status: number
) =>
	useQuery({
		queryKey: [
			EMPLOYEE_DDL_BY_STATUS_DATA_QUERY_KEY,
			clientId,
			taxYear,
			status,
		],
		queryFn: () => {
			return SalaryProfileService.getEmployeeDDLByStatus(
				clientId,
				status,
				taxYear
			);
		},
		refetchOnWindowFocus: false,
	});

const useGetSalaryProfileDDL = (
	clientId: string,
	taxYear: number,
	employeeId: string
) =>
	useQuery({
		queryKey: [SALARY_PROFILE_DDL_BY_EMPLOYEE_ID_DATA_QUERY_KEY, employeeId],
		queryFn: () => {
			return SalaryProfileService.getSalaryProfileDDL(
				// clientId,
				employeeId
				// taxYear
			);
		},
		refetchOnWindowFocus: false,
	});

const useGetProfileAppliesFromPayPeriodDDL = (
	id: string | undefined,
	clientId: string,
	employeePayPeriodId: string,
	taxYear: number
) =>
	useQuery({
		queryKey: [
			PROFILE_APPLIES_FROM_PAY_PERIOD_DDL_DATA_QUERY_KEY,
			employeePayPeriodId,
		],
		queryFn: () => {
			return SalaryProfileService.getProfileAppliesFromPayPeriodDDL(
				id,
				clientId,
				employeePayPeriodId,
				taxYear
			);
		},
		refetchOnWindowFocus: false,
	});

// const useGetEmployeeDDLItems = (clientId: string, taxYear: number) =>
//   useQuery({
//     queryKey: [EMPLOYEE_DDL_DATA_QUERY_KEY, clientId, taxYear],
//     queryFn: (data) => {
//       return SalaryProfileService.getEmployeeDDL(clientId, taxYear);
//     },
//     refetchOnWindowFocus: false,
//   });

const useGetCodeDDL = (provinceName: any, provinceId: any, taxYear: number) =>
	useQuery({
		queryKey: ["wsib-code-ddl", provinceName, provinceId, taxYear],
		queryFn: (data) => {
			return WCBService.getAllProvincesCodesDDL(provinceName, provinceId, taxYear);
		},
		refetchOnWindowFocus: false,
	});

export const useGetSalaryProfilePermissionData = (
	clientId: string,
	navigateToLogout: (path: string) => void,
	employeeId: string | null,
	payrollScheduleId?: string | null,
) =>
	useQuery({
		queryKey: ["get-salary-profile-permission-data", clientId, payrollScheduleId, employeeId],
		queryFn: (data) => {
			return PermissionsService.getSalaryPeofilePermissionData(
				clientId,
				navigateToLogout,
				employeeId,
				payrollScheduleId,
			);
		},
		refetchOnWindowFocus: false,
	});
export const SalaryProfile: FC<ISalaryProfile> = ({ testId }) => {
	const navigate = useNavigate();
	const dispatch = useDispatch();
	const navigateToLogout = useNavigateToLogout();
	const clientName = useSelector((state: IAppState) => state.client.clientName);
	const companyInfo = useSelector((state: IAppState) => state.client);
	const isAdminUser = useSelector(
		(state: IAppState) => state.user.profile.isSuperStatus
	);
	const theme = useTheme();
	const [snackbarData, setSnackbarData] = useState<any>(INITIAL_SNACKBAR_DATA);
	const clientId = useSelector((state: IAppState) => state.client.id);
	const taxYear = useSelector((state: IAppState) => state.taxYear.year);
	const [selectedSalaryProfile, setSelectedSalaryProfile] =
		useState("create-new");

	const [onResourceSelectionAPIsCalls, setOnResourceSelectionAPIsCalls] =
		useState(false);

	// for delete row
	const [openDialog, setDialogOpen] = useState(false);
	const [singleDelete, setSingleDelete] = useState<boolean>(false);
	const [salaryProfileId, setSalaryProfileId] = useState<any>("");
	const [selectedEmployee, setSelectedEmployee] = useState<any>(null);
	const [SalaryProfileRecord, setSalaryProfileRecord] =
		useState<any>(undefined);
	const [employeeStatus, setEmployeeStatus] = useState<number>(100);
	const [provinceId, setProvinceId] = useState<any>(null);
	const [provinceName, setProvinceName] = useState<any>(null);
	const [disabledSubmitButton, setDisabledSubmitButton] = useState<any>(false);

	// for edit
	const queryClient = useQueryClient();
	const { id } = useParams();
	const [editRecordId, setEditRecordId] = useState(id);

	useEffect(() => {
		if (id) {
			setEditRecordId(id);
		}
	}, [id]);


	useEffect(() => {
		if (id && id.startsWith("z_")) {
			let employeeIdFromPayrollScreenAfterClickEmployeeName = id.slice(2);
			setSalaryProfileRecord(undefined)
			formMethods.reset(DEFAULT_VALUES);
			setSelectedEmployee(employeeIdFromPayrollScreenAfterClickEmployeeName);
			navigate(`/employee/salary-profile`);
			setEditRecordId(undefined)
		}
	}, [id]);

	const formInitialState = () => {
		setSalaryProfileRecord(undefined);
		setSelectedSalaryProfile("create-new");
		setEditRecordId(undefined);
		// formMethods.reset();
		let path = "/employee/salary-profile";
		navigate(path);
		// formMethods.setValue('profileAppliesFromPayPeriod',undefined)
		// formMethods.setValue('howIsEmployPaid',200)
		// formMethods.setValue('regularSalaryForThePeriod',null)
		// formMethods.setValue('hourlyRate',null)
		// formMethods.setValue('regularHours',null)
		// formMethods.setValue('minVocationPayOfGross',null)
		// formMethods.setValue('otherFederalTaxCreditsAuthorizedByTaxCenterMedicalExpensesChartiableDonations',null)
		// formMethods.setValue('otherAnnualProvincialNonRefundableTaxCreditsAuthorizedByTaxCenter',null)
		// formMethods.setValue('boughtApprovedSharesOfCapitalStockOfAPrescribedLaborSponsoredVentureCapitalCorp',null)
		// formMethods.setValue('contributionToTheFondsDeSolidariteDesTravailleursDuQuebecFTQOrToFondaction',null)
		// formMethods.setValue('numberOfDisabledDependants',null)
		// formMethods.setValue('noOfDependantsUnderAge18ForWhichTheEmployeeOrHasMadeAWrittenRequest',null)
		// formMethods.setValue('annualDeductionAuthorizedInFormTp1016V',null)
		// formMethods.setValue('tp1016VFilledBeforeFirstPeriodInTheYear',null)
		// formMethods.setValue('cppQppExempt',false)
		// formMethods.setValue('eiExempt',false)
		// formMethods.setValue('federalTaxExempt',false)
		// formMethods.setValue('provisionalTaxExempt',false)
		// formMethods.setValue('ehtOntario',false)
		// formMethods.setValue('ehtBritishColumbia',false)
		// formMethods.setValue('qpipExempt',false)
		// formMethods.setValue('csstExempt',false)
	};

	const DEFAULT_VALUES = {
		// employeeType: 100,
		profileAppliesFromPayPeriod: undefined,
		howIsEmployPaid: 200,
		regularSalaryForThePeriod: null,
		hourlyRate: null,
		regularHours: null,
		minVocationPayOfGross: 4,
		cppQppExempt: false,
		eiExempt: false,
		federalTaxExempt: false,
		provisionalTaxExempt: false,
		ehtOntario: false,
		ehtBritishColumbia: false,
		qpipExempt: false,
		csstExempt: false,
		otherFederalTaxCreditsAuthorizedByTaxCenterMedicalExpensesChartiableDonations:
			null,
		otherAnnualProvincialNonRefundableTaxCreditsAuthorizedByTaxCenter: null,
		boughtApprovedSharesOfCapitalStockOfAPrescribedLaborSponsoredVentureCapitalCorp:
			null,
		contributionToTheFondsDeSolidariteDesTravailleursDuQuebecFTQOrToFondaction:
			null,
		numberOfDisabledDependants: null,
		noOfDependantsUnderAge18ForWhichTheEmployeeOrHasMadeAWrittenRequest: null,
		annualDeductionAuthorizedInFormTp1016V: null,
		tp1016VFilledBeforeFirstPeriodInTheYear: false,
	};

	const formMethods = useForm<any>({
		resolver: yupResolver(SalaryProfileValidationSchema),
		mode: "onBlur",
		reValidateMode: "onChange",
		defaultValues: DEFAULT_VALUES,
	});

	const { isPending: isSubmitting, mutate: submitSalaryProfileFields } =
		useMutation({
			mutationFn: SalaryProfileService.create,

			onSuccess: (
				res: ISalaryProfileRequestPayload | IServerError | IResponse | any
			) => {

				if (res.data) {
					setSalaryProfileRecord(res.data);
					queryClient.invalidateQueries({
						queryKey: [SINGLE_RECORD_DATA_QUERY_KEY, res.data.id],
					});
					queryClient.invalidateQueries({
						queryKey: [
							SALARY_PROFILE_DDL_BY_EMPLOYEE_ID_DATA_QUERY_KEY,
							selectedEmployee,
						],
					});
					queryClient.invalidateQueries({
						queryKey: [
							EDR_EARNINGS_AND_TIME_FORMAT_DATA_QUERY_KEY
						],
					});
					scrollPanelFieldsToTop();
					let path = `/employee/salary-profile/${res.data.id}`;
					navigate(path);
					setSelectedSalaryProfile(res.data.id);
					setEditRecordId(res.data.id);

					setSnackbarData({
						open: true,
						message: "Success",
						severity: "success",
					});
				} else {
					setSnackbarData({
						open: true,
						message: res.message ? res.message : res.data.message,
						severity: "error",
					});
				}
			},
			onError: (err: any) => {

				setSnackbarData({
					open: true,
					message: err.message,
					severity: "error",
				});
			},
		});

	// for edit
	const { isPending: isPendingData, data: selectedSalaryProfileData } =
		useGetData(editRecordId, clientId, taxYear);

	const { isPending: isPendingEmployeeDataById, data: employeeDataById } =
		useGetEmployeeInformationById(selectedEmployee);

	const { isLoading: isLoadingPayrollFrequenciesDDL, data: payrollFrequenciesDDL } =
		useGetPayrollFrequenciesDDL();

	const regularHoursToDefault = (ddl: any, code: number) => {
		if (ddl && code) {
			let employeeCurrentPayFrequency = ddl.find((x: any) => x.code === employeeDataById.employeePayroll.payPeriod)
			let code = employeeCurrentPayFrequency.code
			// switch(code){
			// 	case 12:
			// 		formMethods.setValue('regularHours', 160);
			// 		break;
			// 	case 26:
			// 		formMethods.setValue('regularHours', 80);
			// 		break;
			// 	case 24:
			// 		formMethods.setValue('regularHours', 80);
			// 		break;
			// 	case 52:
			// 		formMethods.setValue('regularHours', 40);
			// 		break;
			// }

			// if (code === 12) {                                // Monthly
			// 	formMethods.setValue('regularHours', 160);
			// } else if (code === 26 || code === 24) {          // 26: Biweekly, 24: Semimonthly
			// 	formMethods.setValue('regularHours', 80);
			// } else if (code === 52) {                         // Weekly
			// 	formMethods.setValue('regularHours', 40);
			// } else {

			// }

			const HOURS_BY_CODE: Record<number, number> = {
				12: 160,  // Monthly
				24: 80,   // Semimonthly
				26: 80,   // Biweekly
				52: 40,   // Weekly
			};

			if (HOURS_BY_CODE[code]) {
				formMethods.setValue('regularHours', HOURS_BY_CODE[code]);
			}

		}
	}

	useEffect(() => {
		if (employeeDataById) {
			setProvinceId(employeeDataById.address.province.id)
			// setProvinceName(employeeDataById.address.province.name === "Alberta" ? "alberta" :
			// 	employeeDataById.address.province.name === "British Columbia" ? "british_columbia" :
			// 		employeeDataById.address.province.name === "Manitoba" ? "manitoba" :
			// 			employeeDataById.address.province.name === "New Brunswick" ? "new_brunswick" :
			// 				employeeDataById.address.province.name === "Newfoundland and Labrador" ? "newfoundland_and_labrador" :
			// 					employeeDataById.address.province.name === "Northwest Territories" ? "northwest_territories" :
			// 						employeeDataById.address.province.name === "Nova Scotia" ? "nova_scotia" :
			// 							employeeDataById.address.province.name === "Nunavut" ? "nunavut" :
			// 								employeeDataById.address.province.name === "Ontario" ? "ontario" :
			// 									employeeDataById.address.province.name === "Prince Edward Island" ? "prince_edward_island" :
			// 										employeeDataById.address.province.name === "Quebec" ? "quebec" :
			// 											employeeDataById.address.province.name === "Saskatchewan" ? "saskatchewan" :
			// 												employeeDataById.address.province.name === "Yukon" ? "yukon" :
			// 													employeeDataById.address.province.name)
			setProvinceName(employeeDataById.address.province.name.toLowerCase().replace(/\s+/g, "_"));

			regularHoursToDefault(payrollFrequenciesDDL, employeeDataById.employeePayroll.payPeriod)

		}
	}, [employeeDataById, payrollFrequenciesDDL])

	// const { isPending: isPendingEmployeeDDLItems, data: employeeDDLItems } =
	//   useGetEmployeeDDLItems(clientId, taxYear);

	const {
		isPending: isPendingGetEmployeeDDLByStatus,
		data: edrEarningsAndTimeFormatData,
	} = useGetEDREarningsAndTimeFormatData(clientId, taxYear);

	const { isPending: isPendingEmployeeDDLByStatus, data: employeeDDLByStatus } =
		useGetEmployeeDDLByStatus(clientId, taxYear, employeeStatus);

	const { isPending: isPendingSalaryProfileDDL, data: salaryProfileDDL } =
		useGetSalaryProfileDDL(clientId, taxYear, selectedEmployee);

	const {
		isPending: isPendingProfileAppliesFromPayPeriodDDL,
		data: profileAppliesFromPayPeriodDDL,
	} = useGetProfileAppliesFromPayPeriodDDL(editRecordId, clientId, selectedEmployee, taxYear);

	useEffect(() => {
		if (selectedSalaryProfileData) {
			setSalaryProfileRecord(selectedSalaryProfileData);
		}
	}, [selectedSalaryProfileData]);

	const {
		isPending: isPendingWSIBCodeDDL,
		data: wsibCodeDDL,
	} = useGetCodeDDL(provinceName, provinceId, taxYear);

	const updatedWsibCodeDDL = useMemo(() => {
		if (wsibCodeDDL && Array.isArray(wsibCodeDDL)) {
			let wsibCodeDDLList: IDropDownListResponse[] = sortDDLByLabel(wsibCodeDDL).map(
				(resourceSlip: IDropDownListResponse) => ({
					label: resourceSlip.label,
					value: resourceSlip.value,
				})
			);

			wsibCodeDDLList.unshift({
				label: "Default Company Code",
				value: 100,
			});
			return wsibCodeDDLList;
		}
	}, [wsibCodeDDL]);


	const { isPending: isPendingSalaryProfilePermissionsData, data: salaryProfilePermissionsData }: any =
		useGetSalaryProfilePermissionData(
			clientId,
			navigateToLogout,
			selectedEmployee,
			null,
		);

	const transformEDRDataForPayload = useCallback(
		(formData: Record<string, number>) => {

			const erdEarning: { id: string, label: string, value: number | null }[] = [];
			const erdDeduction: { id: string, label: string, value: number | null }[] = [];
			const erdReimbursement: { id: string, label: string, value: number | null }[] = [];

			Object.entries(formData).forEach(([key, value]) => {
				if (key.endsWith("-earnings")) {
					erdEarning.push({
						id: key.replace("-earnings", ""),
						label: edrEarningsAndTimeFormatData?.earnings.find((x: any) => x.value === key.replace("-earnings", ""))?.label,
						value: value
					});
				} else if (key.endsWith("-deductions")) {
					erdDeduction.push({
						id: key.replace("-deductions", ""),
						label: edrEarningsAndTimeFormatData?.deductions.find((x: any) => x.value === key.replace("-deductions", ""))?.label,
						value: value,
					});
				} else if (key.endsWith("-reimbursements")) {
					erdReimbursement.push({
						id: key.replace("-reimbursements", ""),
						label: edrEarningsAndTimeFormatData?.reimbursements.find((x: any) => x.value === key.replace("-reimbursements", ""))?.label,
						value: value,
					});
				}
			});

			return { erdEarning, erdDeduction, erdReimbursement };
		},
		[edrEarningsAndTimeFormatData]
	);

	// const transformEDRDataForPayload = useCallback(
	// 	(formData: Record<string, number | null>) => {
	// 		const safeGetLabel = (
	// 			arr: { label: string; value: string }[] | undefined,
	// 			id: string
	// 		): string => arr?.find((x) => x.value === id)?.label ?? "";

	// 		const erdEarning: { id: string; label: string; value: number | null }[] = [];
	// 		const erdDeduction: { id: string; label: string; value: number | null }[] = [];
	// 		const erdReimbursement: { id: string; label: string; value: number | null }[] = [];

	// 		Object.entries(formData).forEach(([key, value]) => {
	// 			const id = key.split("-")[0];

	// 			if (key.endsWith("-earnings")) {
	// 				erdEarning.push({
	// 					id,
	// 					label: safeGetLabel(edrEarningsAndTimeFormatData?.earnings, id),
	// 					value: value ?? null,
	// 				});
	// 			} else if (key.endsWith("-deductions")) {
	// 				erdDeduction.push({
	// 					id,
	// 					label: safeGetLabel(edrEarningsAndTimeFormatData?.deductions, id),
	// 					value: value ?? null,
	// 				});
	// 			} else if (key.endsWith("-reimbursements")) {
	// 				erdReimbursement.push({
	// 					id,
	// 					label: safeGetLabel(edrEarningsAndTimeFormatData?.reimbursements, id),
	// 					value: value ?? null,
	// 				});
	// 			}
	// 		});

	// 		return { erdEarning, erdDeduction, erdReimbursement };
	// 	},
	// 	[edrEarningsAndTimeFormatData]
	// );

	const limits: Record<string, number> = {
		annually: 8760,
		monthly: 744,
		biweekly: 336,
		weekly: 168,
		"semi monthly": 384,
	};

	const validateRegularHours = (hours: number, frequency: any): boolean => {
		const maxLimit = limits[frequency];
		if (!maxLimit) return false;

		const intPart = Math.floor(hours);
		const isValid = intPart <= maxLimit;

		if (!isValid) {
			setSnackbarData({
				open: true,
				message: `Only values from 0.00 to ${maxLimit} are allowed for "Regular Hours ".`,
				severity: "error",
			});
		}

		return isValid;
	};

	const convertRegularHoursMinutesToFractions = (
		hours: number,
		timeFormat: any
	) => {
		const [intPart, decimalPart] = hours.toString().split(".");

		// If no decimal, just return the hours
		if (!decimalPart) return hours;

		const normalizedDecimal = decimalPart.padEnd(2, "0");
		const minutesOrFraction = parseInt(normalizedDecimal, 10);

		if (timeFormat === 200) {
			if (minutesOrFraction > 59) {
				setSnackbarData({
					open: true,
					message: `Only values from .00 to .59 are allowed for "Minutes Time Format".`,
					severity: "error",
				});
				return null;
			}
			// convert minutes to fraction (e.g. 26 → 26/60 = 0.4333)
			return parseFloat(
				(parseInt(intPart, 10) + minutesOrFraction / 60).toFixed(2)
			);
		}

		return null;
	};

	const checkMinutesValidity = (hours: number) => {
		const [intPart, decimalPart] = hours.toString().split(".");

		// If no decimal, just return the hours
		if (!decimalPart) return hours;

		const normalizedDecimal = decimalPart.padEnd(2, "0");
		const minutesOrFraction = parseInt(normalizedDecimal, 10);

		if (minutesOrFraction > 59) {
			setSnackbarData({
				open: true,
				message: `Only values from .00 to .59 are allowed for "Minutes Time Format".`,
				severity: "error",
			});
			return false;
		} else {
			return true;
		}
	};

	const convertRegularHoursFormat = (hours: number, timeFormat: any) => {
		const [intPart, decimalPart] = hours.toString().split(".");

		// If no decimal, just return the hours
		if (!decimalPart) return hours;

		const normalizedDecimal = decimalPart.padEnd(2, "0");
		const minutesOrFraction = parseInt(normalizedDecimal, 10);

		if (timeFormat === 100) {
			return parseFloat(
				(parseInt(intPart, 10) + minutesOrFraction * 60).toFixed(2)
			);
		} else if (timeFormat === 200) {
			return parseFloat(
				(parseInt(intPart, 10) + minutesOrFraction / 60).toFixed(2)
			);
		}

		return hours;
	};


	const timeFormat =
		edrEarningsAndTimeFormatData && edrEarningsAndTimeFormatData.timeFormat;
	const onSubmit = async (formData: any) => {


		//getting time format from general setting
		// minutes = 200
		// fractions = 100
		const requestPayload: any = {
			clientId: clientId,
			taxYear: taxYear,
			id:
				selectedSalaryProfileData &&
					selectedSalaryProfileData.id &&
					selectedSalaryProfileData.date === formData.profileAppliesFromPayPeriod
					? selectedSalaryProfileData.id
					: SalaryProfileRecord &&
						SalaryProfileRecord.id &&
						SalaryProfileRecord.date === formData.profileAppliesFromPayPeriod
						? SalaryProfileRecord.id
						: undefined,

			employeeId: selectedEmployee,
			date: formData.profileAppliesFromPayPeriod,
			howIsEmployPaid: formData.howIsEmployPaid,
			regularSalaryForThePeriod: formData.regularSalaryForThePeriod
				? formData.regularSalaryForThePeriod
				: 0,
			hourlyRate: formData.hourlyRate ? formData.hourlyRate : 0,
			regularHours:
				timeFormat === 100 &&
					formData.regularHours &&
					formData.regularHours !== null
					? convertRegularHoursFormat(formData.regularHours, timeFormat)
					: formData.regularHours,
			minVocationPayOfGross: formData.minVocationPayOfGross
				? formData.minVocationPayOfGross
				: 0,
			wsibCode: formData.wsibCode,
			otherFederalTaxCreditsAuthorizedByTaxCenterMedicalExpensesChartiableDonations:
				formData.otherFederalTaxCreditsAuthorizedByTaxCenterMedicalExpensesChartiableDonations,
			boughtApprovedSharesOfCapitalStockOfAPrescribedLaborSponsoredVentureCapitalCorp:
				formData.boughtApprovedSharesOfCapitalStockOfAPrescribedLaborSponsoredVentureCapitalCorp,
			otherAnnualProvincialNonRefundableTaxCreditsAuthorizedByTaxCenter:
				formData.otherAnnualProvincialNonRefundableTaxCreditsAuthorizedByTaxCenter,
			contributionToTheFondsDeSolidariteDesTravailleursDuQuebecFTQOrToFondaction:
				employeeDataById &&
					employeeDataById.employeePayroll.provinceOfEmployment.shortCode &&
					employeeDataById.employeePayroll.provinceOfEmployment.shortCode === "QC"
					? formData.contributionToTheFondsDeSolidariteDesTravailleursDuQuebecFTQOrToFondaction
					: 0,
			numberOfDisabledDependants: formData.numberOfDisabledDependants,
			noOfDependantsUnderAge18ForWhichTheEmployeeOrHasMadeAWrittenRequest:
				formData.noOfDependantsUnderAge18ForWhichTheEmployeeOrHasMadeAWrittenRequest,
			annualDeductionAuthorizedInFormTp1016V:
				employeeDataById &&
					employeeDataById.employeePayroll.provinceOfEmployment.shortCode &&
					employeeDataById.employeePayroll.provinceOfEmployment.shortCode === "QC"
					? formData.annualDeductionAuthorizedInFormTp1016V
					: 0,
			tp1016VFilledBeforeFirstPeriodInTheYear:
				employeeDataById &&
					employeeDataById.employeePayroll.provinceOfEmployment.shortCode &&
					employeeDataById.employeePayroll.provinceOfEmployment.shortCode === "QC"
					? formData.tp1016VFilledBeforeFirstPeriodInTheYear
					: false,
			cppQppExempt: formData.cppQppExempt,
			eiExempt: formData.eiExempt,
			federalTaxExempt: formData.federalTaxExempt,
			provisionalTaxExempt: formData.provisionalTaxExempt,
			qpipExempt:
				employeeDataById &&
					employeeDataById.employeePayroll.provinceOfEmployment.shortCode &&
					employeeDataById.employeePayroll.provinceOfEmployment.shortCode === "QC"
					? formData.qpipExempt
					: false,
			csstExempt:
				employeeDataById &&
					employeeDataById.employeePayroll.provinceOfEmployment.shortCode &&
					employeeDataById.employeePayroll.provinceOfEmployment.shortCode === "QC"
					? formData.csstExempt
					: false,
			wsibWcbExemptAs: formData.wsibWcbExemptAs,
			ehtOntarioExempt: formData.ehtOntarioExempt,
			ehtBcExempt: formData.ehtBcExempt,
			erdEarning: transformEDRDataForPayload(formData).erdEarning,
			erdReimbursement: transformEDRDataForPayload(formData).erdReimbursement,
			erdDeduction: transformEDRDataForPayload(formData).erdDeduction,
		};

		if (formData.regularHours && formData.regularHours !== null) {

			if (validateRegularHours(formData.regularHours, "monthly")) {
				if (timeFormat === 200) {
					if (checkMinutesValidity(formData.regularHours)) {
						submitSalaryProfileFields(requestPayload);
					}
				} else {
					submitSalaryProfileFields(requestPayload);
				}
			}
		} else {
			submitSalaryProfileFields(requestPayload);
		}
	};

	const onResetClicked = () => {
		setSnackbarData({
			open: true,
			message: "Success",
			severity: "success",
		});
		formInitialState();
		formMethods.reset();
		scrollPanelFieldsToTop();
		setSelectedEmployee(null)
	};

	// For Delete Row

	const { isPending: isDeleting, mutate: deleteSalaryProfileById } =
		useMutation({
			mutationFn: SalaryProfileService.delSalaryProfile,

			onSuccess: (res: any | IServerError | IResponse) => {
				if (typeof res === "string") {
					setSnackbarData({
						open: true,
						message: "Success",
						severity: "success",
					});
					setSalaryProfileRecord(undefined);
					queryClient.invalidateQueries({
						queryKey: [
							SALARY_PROFILE_DDL_BY_EMPLOYEE_ID_DATA_QUERY_KEY,
							selectedEmployee,
						],
					});
					scrollPanelFieldsToTop();
					setSelectedSalaryProfile("create-new");
				} else {
					setSnackbarData({
						open: true,
						message: res.message,
						severity: "error",
					});
				}
			},
			onError: (err: any) => {
				setSnackbarData({
					open: true,
					message: err.message,
					severity: "error",
				});
			},
		});

	const deleteSalaryProfileField = () => {
		if (singleDelete) {
			let payload = {
				salaryProfileId,
				clientId,
				taxYear,
			};
			setSingleDelete(false);
			deleteSalaryProfileById(payload);
			setDialogOpen(false);
		}
	};

	const onDeleteClicked = () => {
		setDialogOpen(true);
		setSingleDelete(true);
		if (
			selectedSalaryProfileData &&
			selectedSalaryProfileData.resource
			// && editedRecordSlipCreateNew === false
		) {
			setSalaryProfileId(selectedSalaryProfileData.id);
		}
	};
	/**
	 * On Snackbar closed.
	 *
	 * @param {React.SyntheticEvent | Event} event
	 * @param {string} reason
	 */
	const onSnackbarClosed = (
		event: React.SyntheticEvent | Event,
		reason?: string
	) => {
		if (reason === "clickaway") {
			return;
		}
		setSnackbarData(INITIAL_SNACKBAR_DATA);
	};

	const onEmployeeSelectionChangeHandler = async (
		selectedItem: IEnhancedDropDownItem | any
	): Promise<void> => {


		setSalaryProfileRecord(undefined)
		formMethods.reset(DEFAULT_VALUES);
		setSelectedEmployee(selectedItem.value);
		if (selectedEmployee !== selectedItem.value) {
			let path = `/employee/salary-profile`;
			navigate(path);
			setEditRecordId(undefined)
		}
	};

	const onSalaryProfileSelectionChangeHandler = (
		selectedItem: IEnhancedDropDownItem | any
	) => {

		setOnResourceSelectionAPIsCalls(true);
		if (selectedItem.value === "create-new") {
			setSelectedSalaryProfile('create-new')
			formMethods.reset()
			// formInitialState();
			// formMethods.reset(DEFAULT_VALUES)
			// setSelectedEmployee(null)

			setSalaryProfileRecord(undefined);
			setSelectedSalaryProfile("create-new");
			setEditRecordId(undefined);
			let path = "/employee/salary-profile";
			navigate(path);
			// formMethods.setValue('profileAppliesFromPayPeriod',undefined)
			if (employeeDataById) regularHoursToDefault(payrollFrequenciesDDL, employeeDataById.employeePayroll.payPeriod)
		}
		if (selectedItem.value !== "create-new") {
			setSelectedSalaryProfile(selectedItem.value)
			setEditRecordId(selectedItem.value);
			let path = `/employee/salary-profile/${selectedItem.value}`;
			navigate(path);
		}
	};

	useEffect(() => {

		if (id && SalaryProfileRecord) {
			formMethods.clearErrors()
			formMethods.reset(DEFAULT_VALUES)
			Object.keys(SalaryProfileRecord).forEach((fieldName) => {
				const fieldValue = SalaryProfileRecord[fieldName];

				if (fieldName === "employeeId") {
					formMethods.setValue("employee", fieldValue);
					setSelectedEmployee(fieldValue);

				}
				if (fieldName === "id") {
					setSelectedSalaryProfile(fieldValue);
					formMethods.setValue("salaryProfile", fieldValue);
				}
				if (fieldName === "erdEarning" && fieldValue && fieldValue.length > 0) {
					fieldValue.map((item: any) => {
						formMethods.setValue(`${item.id}-earnings`, item.value);
					});
				}
				if (fieldName === "erdReimbursement" && fieldValue && fieldValue.length > 0) {
					fieldValue.map((item: any) => {
						formMethods.setValue(`${item.id}-reimbursements`, item.value);
					});
				}
				if (fieldName === "erdDeduction" && fieldValue && fieldValue.length > 0) {
					fieldValue.map((item: any) => {
						formMethods.setValue(`${item.id}-deductions`, item.value);
					});
				}
				if (fieldName === "date" && profileAppliesFromPayPeriodDDL) {
					formMethods.setValue("profileAppliesFromPayPeriod", fieldValue);
				}
				if (fieldName === "regularHours") {
					if (timeFormat === 100) {
						const value = convertRegularHoursFormat(fieldValue, timeFormat);
						formMethods.setValue(fieldName, value);
					} else {
						formMethods.setValue(fieldName, fieldValue);
					}
				}
				if (typeof fieldValue !== "object" && !Array.isArray(fieldValue)) {
					formMethods.setValue(fieldName, fieldValue ?? undefined);
				}
			});
		}
	}, [
		SalaryProfileRecord,
		formMethods.setValue,
		formMethods,
		id,
		selectedEmployee,
		profileAppliesFromPayPeriodDDL,
	]);

	const onEmployeeTypeSelectionChangeHandler = (event: any) => {
		// formMethods.setValue('employeeType',event.value)
		formInitialState();
		setEmployeeStatus(event.value);
		setSelectedEmployee(null);
	};

	const onCloseHandler = () => {
		setDialogOpen(false);
	};

	const updatedSalaryProfileDDL = useMemo(() => {
		if (salaryProfileDDL && Array.isArray(salaryProfileDDL)) {
			let salaryProfileDDLList: IDropDownListResponse[] = sortDDLByLabel(salaryProfileDDL).map(
				(resourceSlip: IDropDownListResponse) => ({
					label: resourceSlip.label,
					value: resourceSlip.value,
				})
			);

			salaryProfileDDLList.unshift({
				label: "Create New",
				value: "create-new",
			});

			if (editRecordId) {
				setSelectedSalaryProfile(String(editRecordId));
			} else if (salaryProfileDDL && salaryProfileDDL.length > 0) {
				const lastIndex = salaryProfileDDL.length - 1;
				setSelectedSalaryProfile(String(salaryProfileDDL[lastIndex].value));
				setEditRecordId(salaryProfileDDL[lastIndex].value)
				let path = `/employee/salary-profile/${salaryProfileDDL[lastIndex].value}`;
				navigate(path);
			} else {
				setSalaryProfileRecord(undefined)
				setSelectedSalaryProfile("create-new");
				let path = `/employee/salary-profile`;
				navigate(path);
				formMethods.reset();
				if (employeeDataById) regularHoursToDefault(payrollFrequenciesDDL, employeeDataById.employeePayroll.payPeriod)
			}
			setSalaryProfileRecord(undefined);
			return salaryProfileDDLList;
		}
	}, [salaryProfileDDL, selectedEmployee]);

	const tableScrollRef = useRef<HTMLDivElement>(null);
	const upadtedEmployeeDDLByStatusItems = useMemo(() => {
		return (
			employeeDDLByStatus &&
			sortDDLByLabel(employeeDDLByStatus).map((item: any) => ({
				...item,
				label: (
					<span
						style={{ color: theme.palette.primary.light, cursor: "pointer" }}
						onClick={() => {
							onEmployeeSelectionChangeHandler(item);
							scrollPanelFieldsToTop();
						}}
					>
						{item.label}
					</span>
				),
			}))
		);
	}, [employeeDDLByStatus]);

	const onProfileAppliesFromPayPeriodChangeHandler = (e: any) => {
		let selected = e.value
		if (id && SalaryProfileRecord && SalaryProfileRecord.id) {
			const lastPeriodProcessed = salaryProfilePermissionsData && salaryProfilePermissionsData.lastPeriodProcessed
			if (selected !== lastPeriodProcessed) {
				setDisabledSubmitButton(false)
			} else {
				setDisabledSubmitButton(true)
			}
		}
	}

	const drawerMenuOptions = useSelector(
		(state: IAppState) => state.drawerMenu.menu
	);

	const expandedLabels = ["Employee"];
	const expandedT4SetupItem = "Salary";
	const enhanceData = (data: any[], parentLabel?: string): any[] => {
		return data.map((item) => {
			let isExpanded = expandedLabels.includes(item.label);
			if (parentLabel === "Employee" && item.label === expandedT4SetupItem) {
				isExpanded = true;
			}
			const enhancedItem = {
				...item,
				expanded: item.items ? isExpanded : false,
				items: item.items ? enhanceData(item.items, item.label) : undefined,
				path: item.label === expandedT4SetupItem && "/employee/salary-profile",
			};
			return enhancedItem;
		});
	};

	useEffect(() => {
		dispatch(setSelectedStickyNavBar({ isSticky: true }));
		if (drawerMenuOptions && drawerMenuOptions.length > 0) {
			const enhancedData = enhanceData(drawerMenuOptions);
			dispatch(setSelectedDrawerOptionsMenu({ menu: enhancedData }));
		}
	}, [drawerMenuOptions]);


	return (
		<>
			<Grid size={{ xs: 12, sm: 12, md: 12, lg: 12, xl: 12 }}>
				<BackdropCircularProgress
					open={
						// isPendingEmployeeDDLItems ||
						isSubmitting ||
						isPendingData ||
						isDeleting ||
						isPendingEmployeeDDLByStatus ||
						isPendingProfileAppliesFromPayPeriodDDL ||
						isPendingGetEmployeeDDLByStatus ||
						isPendingEmployeeDataById ||
						isPendingSalaryProfileDDL ||
						isPendingWSIBCodeDDL ||
						isLoadingPayrollFrequenciesDDL ||
						isPendingSalaryProfilePermissionsData
					}
					testId={testId}
				/>
				<DeleteConfirmationDialog
					open={openDialog}
					deleteActionHandler={deleteSalaryProfileField}
					cancelActionHandler={onCloseHandler}
					testId="T4-confirmation-dialog"
				/>
				<FormProvider {...formMethods}>
					<form onSubmit={formMethods.handleSubmit(onSubmit)}>
						<Grid container size={{ xs: 12, sm: 12, md: 12, lg: 12, xl: 12 }}>
							<Grid
								size={{ xs: 12, sm: 12, md: 8.5, lg: 8, xl: 8 }}
								sx={{
									borderRadius: "3px",
								}}
							>
								<TwoColumnLayout
									// <TopRowTwoColumnLayout
									// topRowContent={
									// <StandardPanel
									//   title=""
									//   testId={`${testId}-slip-data`}
									//   showTitle={false}
									// >
									//   <Grid
									//     container
									//     size={{ xs: 12, sm: 12, md: 12, lg: 12, xl: 12 }}
									//     columnSpacing={3}
									//   >
									//     <Grid
									//       size={{ xs: 12, sm: 4, md: 3, lg: 3, xl: 3 }}
									//       sx={{ paddingTop: "1rem" }}
									//     >
									//       <EnhancedDropDownRHF
									//         selectName="employeeType"
									//         testId={`employee-type`}
									//         items={[
									//           {
									//             label: "List All Employees",
									//             value: "all",
									//           },
									//           {
									//             label: "List Active Employees Only",
									//             value: 100,
									//           },
									//           {
									//             label: "List Inactive Employees Only",
									//             value: 200,
									//           },
									//           {
									//             label: "List Terminated Employees Only",
									//             value: 300,
									//           },
									//           {
									//             label: "List Died Employees Only",
									//             value: 400,
									//           },
									//         ]}
									//         selectedItem={100}
									//         onSelectionChangeHandler={
									//           onEmployeeTypeSelectionChangeHandler
									//         }
									//       />
									//       <EnhancedDropDownRHF
									//         selectName="employee"
									//         testId={`employee`}
									//         items={employeeDDLByStatus ?? []}
									//         onSelectionChangeHandler={
									//           onEmployeeSelectionChangeHandler
									//         }
									//       />
									//       <EnhancedDropDownRHF
									//         selectName="salaryProfile"
									//         testId={`salaryProfile`}
									//         items={updatedSalaryProfileDDL ?? []}
									//         selectedItem={selectedSalaryProfile}
									//         onSelectionChangeHandler={
									//           onSalaryProfileSelectionChangeHandler
									//         }
									//       />
									//     </Grid>
									//     <Grid
									//       size={{
									//         xs: 12,
									//         sm: 12,
									//         md: 2.45,
									//         lg: 2.45,
									//         xl: 2.45,
									//       }}
									//       sx={{
									//         color: "rgba(0, 0, 0, 0.6)",
									//         fontSize: "0.875rem",
									//         paddingTop: "6px",
									//       }}
									//     >
									//       Province AB
									//       <br />
									//       Pay Period Monthly
									//       <br />
									//       First Pay Date Feb 05 2025 <br />
									//       Period IE 0.00
									//     </Grid>
									//     <Grid
									//       size={{
									//         xs: 12,
									//         sm: 12,
									//         md: 2.45,
									//         lg: 2.45,
									//         xl: 2.45,
									//       }}
									//       sx={{
									//         color: "rgba(0, 0, 0, 0.6)",
									//         fontSize: "0.875rem",
									//         paddingTop: "6px",
									//       }}
									//     >
									//       Annual Taxable Income 0.00
									//       <br />
									//       Period Taxable Salary 0.00
									//       <br />
									//       Fed Claim 0.00
									//       <br />
									//       Prov Claim 0.00
									//     </Grid>
									//     <Grid
									//       size={{
									//         xs: 12,
									//         sm: 12,
									//         md: 2.05,
									//         lg: 2.05,
									//         xl: 2.05,
									//       }}
									//       sx={{
									//         color: "rgba(0, 0, 0, 0.6)",
									//         fontSize: "0.875rem",
									//         paddingTop: "6px",
									//       }}
									//     >
									//       CPP 0.00 <br />
									//       EI 0.00
									//       <br />
									//       Fed Tax 0.00
									//       <br />
									//       Prov Tax 0.00
									//     </Grid>
									//     <Grid
									//       size={{
									//         xs: 12,
									//         sm: 12,
									//         md: 2.05,
									//         lg: 2.05,
									//         xl: 2.05,
									//       }}
									//       sx={{
									//         color: "rgba(0, 0, 0, 0.6)",
									//         fontSize: "0.875rem",
									//         paddingTop: "6px",
									//       }}
									//     >
									//       Add. Tax 0.00 <br />
									//       Total Ded. 0.00
									//       <br />
									//       Other Ded. 0.00
									//       <br />
									//       Net Salary 0.00
									//     </Grid>
									//   </Grid>
									// </StandardPanel>
									// }
									toolbar={
										<SalaryProfileToolbar
											onResetClicked={onResetClicked}
											// onDeleteClicked={onDeleteClicked}
											isSubmitting={false}
											formMethods={formMethods}
											pageTitle="Salary"
											salaryProfilePermissionsData={salaryProfilePermissionsData && salaryProfilePermissionsData}
											selectedSalaryProfile={selectedSalaryProfile && selectedSalaryProfile}
										/>
									}
									leftColumnContent={
										<>
											<StandardPanel
												title="Determining Gross Income"
												testId={`${testId}-slip-data`}
												borderHide={true}
												borderBottom={true}
											>
												<DeterminingGrossIncomeSetupFieldsPanel
													profileAppliesFromPayPeriodDDL={
														profileAppliesFromPayPeriodDDL ?? []
													}
													SalaryProfileRecord={id && SalaryProfileRecord && SalaryProfileRecord}
													id={id}
													wsibCodeDDL={updatedWsibCodeDDL}
													onProfileAppliesFromPayPeriodChangeHandler={onProfileAppliesFromPayPeriodChangeHandler}
													employeeDDL={employeeDDLByStatus && employeeDDLByStatus}
													onEmployeeSelectionChangeHandler={onEmployeeSelectionChangeHandler}
													updatedSalaryProfileDDL={updatedSalaryProfileDDL}
													onSalaryProfileSelectionChangeHandler={onSalaryProfileSelectionChangeHandler}
													employeeStatus={employeeStatus}
													formMethods={formMethods}
													selectedResourceSlip={selectedSalaryProfile}
													onEmployeeTypeSelectionChangeHandler={onEmployeeTypeSelectionChangeHandler}
												/>
											</StandardPanel>
											<StandardPanel
												title="Tax Exemptions"
												testId={`${testId}-slip-data`}
												borderHide={true}
												borderBottom={
													edrEarningsAndTimeFormatData &&
														edrEarningsAndTimeFormatData.deductions &&
														edrEarningsAndTimeFormatData.deductions.length > 0
														? true
														: false
												}
											>
												<TaxExemptionsSetupFieldsPanel
													employeeDataById={employeeDataById}
												/>
											</StandardPanel>
											{edrEarningsAndTimeFormatData &&
												edrEarningsAndTimeFormatData.deductions &&
												edrEarningsAndTimeFormatData.deductions.length > 0 && (
													<StandardPanel
														title="Deductions"
														testId={`${testId}-slip-data`}
														borderHide={true}
													>
														<DeductionsSetupFieldsPanel
															deductionsFieldsList={
																edrEarningsAndTimeFormatData.deductions
															}
														/>
													</StandardPanel>
												)}
										</>
									}
									rightColumnContent={
										<>
											<StandardPanel
												title="Tax Credits"
												testId={`${testId}-slip-data`}
												borderHide={true}
												borderBottom={
													edrEarningsAndTimeFormatData &&
														edrEarningsAndTimeFormatData.earnings &&
														edrEarningsAndTimeFormatData.earnings.length > 0
														? true
														: edrEarningsAndTimeFormatData &&
															edrEarningsAndTimeFormatData.reimbursements &&
															edrEarningsAndTimeFormatData.reimbursements
																.length > 0
															? true
															: false
												}
											>
												<TaxCreditsSetupFieldsPanel
													employeeDataById={employeeDataById}
												/>
											</StandardPanel>
											{edrEarningsAndTimeFormatData &&
												edrEarningsAndTimeFormatData.earnings &&
												edrEarningsAndTimeFormatData.earnings.length > 0 && (
													<StandardPanel
														title="Earnings"
														testId={`${testId}-slip-data`}
														borderHide={true}
														borderBottom={
															edrEarningsAndTimeFormatData &&
																edrEarningsAndTimeFormatData.reimbursements &&
																edrEarningsAndTimeFormatData.reimbursements
																	.length > 0
																? true
																: false
														}
													>
														<EarningsSetupFieldsPanel
															earningsFieldsList={
																edrEarningsAndTimeFormatData.earnings
															}
														/>
													</StandardPanel>
												)}
											{edrEarningsAndTimeFormatData &&
												edrEarningsAndTimeFormatData.reimbursements &&
												edrEarningsAndTimeFormatData.reimbursements.length >
												0 && (
													<StandardPanel
														title="Reimbursements"
														testId={`${testId}-slip-data`}
														borderHide={true}
													>
														<ReimbursementsSetupFieldsPanel
															reimbursementsFieldsList={
																edrEarningsAndTimeFormatData.reimbursements
															}
														/>
													</StandardPanel>
												)}
										</>
									}
									// showTwoColumnCombineBorder={true}
									testId="t4-slip-setup-form"
								/>
							</Grid>
							<Grid size={{ xs: 12, sm: 12, md: 0.2, lg: 1, xl: 1 }}></Grid>
							<Grid
								size={{ xs: 12, sm: 12, md: 3.3, lg: 3, xl: 3 }}
								// sx={{ marginTop: "16px" }}
								sx={{ marginTop: "16px", display: { xs: "none", sm: "none", md: "block" } }}
							>
								<ResourceDDLDataTableForSlips
									rows={
										employeeDDLByStatus && upadtedEmployeeDDLByStatusItems
											? upadtedEmployeeDDLByStatusItems
											: []
									}
									headerField={
										<EnhancedDropDownRHF
											selectName="employeeType"
											testId={`employee-type`}
											items={[
												{
													label: "List All Employees",
													value: "all",
												},
												{
													label: "List Active Employees Only",
													value: 100,
												},
												{
													label: "List Inactive Employees Only",
													value: 200,
												},
												{
													label: "List Terminated Employees Only",
													value: 300,
												},
												{
													label: "List Died Employees Only",
													value: 400,
												},
											]}
											selectedItem={employeeStatus}
											onSelectionChangeHandler={
												onEmployeeTypeSelectionChangeHandler
											}
										/>
									}
									selectedEmployee={selectedEmployee}
									selectedResourceSlipList={updatedSalaryProfileDDL ?? []}
									selectedResourceSlip={selectedSalaryProfile}
									onResourceSlipSelectionChangeHandler={
										onSalaryProfileSelectionChangeHandler
									}
									tableScrollHeight={"60vh"}
									// selectedId={selectedSalaryProfileData && id && selectedSalaryProfileData?.employeeId}
									selectedId={selectedEmployee && selectedEmployee}
									tableContainerRef={tableScrollRef}
								/>
								<></>
							</Grid>
						</Grid>
					</form>
				</FormProvider>
				<EnhancedSnackbar
					message={snackbarData.message}
					onCloseHandler={onSnackbarClosed}
					severity={snackbarData.severity}
					testId={`${testId}-snackbar`}
					open={snackbarData.open}
					autoHide={snackbarData.autoHide}
				/>
			</Grid>
		</>
	);
};
